// quiz-v2.js
// Quiz v2 engine — reads config from QUIZ_CONFIG (quiz-v2-config.js must load first).
// All classes use the .quiz-v2__ BEM namespace.

// ----------------------------------------------------------------------------
// QuizSession — sessionStorage persistence (key: quiz_v2_session)
// ----------------------------------------------------------------------------

const QuizSession = (function () {
  const SESSION_KEY = 'quiz_v2_session';

  function save(answers, currentIndex) {
    try {
      sessionStorage.setItem(SESSION_KEY, JSON.stringify({ answers, currentIndex }));
    } catch (e) {
      console.warn('[QuizV2] sessionStorage save failed', e);
    }
  }

  function restore() {
    try {
      const raw = sessionStorage.getItem(SESSION_KEY);
      if (!raw) return null;
      const parsed = JSON.parse(raw);
      if (!parsed || typeof parsed.answers !== 'object') return null;
      return parsed;
    } catch (e) {
      return null;
    }
  }

  function clear() {
    try { sessionStorage.removeItem(SESSION_KEY); } catch (e) { /* non-blocking */ }
  }

  return { save, restore, clear };
})();

// ----------------------------------------------------------------------------
// QuizCriteria — criteria evaluation and active question list builder
// ----------------------------------------------------------------------------

const QuizCriteria = (function () {
  // Coerce a stored answer to an array for intersection checks.
  // Handles: string (single-choice), array (multi-select / ranked-multi-select),
  // undefined/null (not yet answered).
  function toArray(value) {
    if (Array.isArray(value)) return value;
    if (value === null || value === undefined || value === '') return [];
    return [value];
  }

  // Returns true if the question should be shown given current answers.
  function evaluate(question, answers) {
    if (!question.criteria || question.criteria.length === 0) return true;

    const check = (item) => {
      const stored = toArray(answers[item.question]);
      return item.options.some(opt => stored.indexOf(opt) !== -1);
    };

    return question.criteriaIsAnd
      ? question.criteria.every(check)   // AND — all items must match
      : question.criteria.some(check);   // OR  — any item must match
  }

  // Returns the filtered + ordered list of questions to show.
  function buildActiveList(allQuestions, answers, isRetake) {
    return allQuestions
      .filter(q => {
        if (isRetake && q.skipForLoggedIn) return false;
        return evaluate(q, answers);
      })
      .slice()
      .sort((a, b) => a.order - b.order);
  }

  return { evaluate, buildActiveList };
})();

// ----------------------------------------------------------------------------
// QuizState — in-memory state
// ----------------------------------------------------------------------------

const QuizState = {
  answers: {},           // { question_short: string | string[] }
  currentIndex: 0,       // index into activeQuestions
  activeQuestions: [],   // filtered + sorted question list
};

// ----------------------------------------------------------------------------
// QuizRenderer — DOM rendering per question type
// ----------------------------------------------------------------------------

const QuizRenderer = (function () {
  let sectionAdvanceTimer = null;

  // Replace {first_name} token with the stored value.
  function resolveText(text) {
    if (!text) return '';
    const name = QuizState.answers.first_name || '';
    return text.replace(/\{first_name\}/g, name);
  }

  // Convert disclaimer subtext into safe HTML paragraphs, linkifying the
  // [Data Privacy Policy] token.
  function linkifySubtext(subtext, privacyPolicyUrl) {
    if (!subtext) return '';
    return subtext
      .split('\n')
      .map(line => {
        // Escape HTML in line
        let safe = line
          .replace(/&/g, '&amp;')
          .replace(/</g, '&lt;')
          .replace(/>/g, '&gt;');
        // Replace token with anchor
        if (privacyPolicyUrl) {
          safe = safe.replace(
            /\[Data Privacy Policy\]/g,
            `<a href="${privacyPolicyUrl}" target="_blank" rel="noopener noreferrer">Data Privacy Policy</a>`
          );
        }
        return `<p class="quiz-v2__subtext-para">${safe}</p>`;
      })
      .join('');
  }

  function renderProgress(current, total, isSection) {
    const container = document.querySelector('.quiz-v2__progress-container');
    if (!container) return;
    if (isSection) {
      container.style.visibility = 'hidden';
      return;
    }
    container.style.visibility = '';
    const progress = total > 0 ? current / total : 0;
    for (let i = 1; i <= 5; i++) {
      const fill = Math.min(Math.max((progress - (i - 1) / 5) * 5, 0), 1) * 100;
      const rect = document.getElementById('quiz-v2-progress-bar-' + i);
      if (rect) rect.setAttribute('width', String(fill));
    }
  }

  // ── Single-choice ──────────────────────────────────────────────────────────

  function renderSingleChoice(q, root, savedAnswer) {
    const optionsEl = document.createElement('div');
    optionsEl.className = 'quiz-v2__options';

    q.options.forEach(option => {
      const btn = document.createElement('button');
      btn.type = 'button';
      btn.className = 'quiz-v2__option';
      if (savedAnswer === option.short) btn.classList.add('quiz-v2__option--selected');
      btn.dataset.value = option.short;
      btn.textContent = option.text;
      btn.addEventListener('click', () => QuizNavigation.next(option.short));
      optionsEl.appendChild(btn);
    });

    root.appendChild(optionsEl);
  }

  // ── Multi-select ───────────────────────────────────────────────────────────

  function renderMultiSelect(q, root, savedAnswer) {
    const selected = Array.isArray(savedAnswer) ? savedAnswer.slice() : [];

    const optionsEl = document.createElement('div');
    optionsEl.className = 'quiz-v2__options quiz-v2__options--multi';
    if (q.twoColumn) optionsEl.classList.add('quiz-v2__options--two-col');

    const nextBtn = document.createElement('button');
    nextBtn.type = 'button';
    nextBtn.className = 'quiz-v2__next-btn';
    nextBtn.textContent = 'Continue';
    nextBtn.disabled = selected.length === 0;
    nextBtn.addEventListener('click', () => QuizNavigation.next(selected.slice()));

    q.options.forEach(option => {
      const btn = document.createElement('button');
      btn.type = 'button';
      btn.className = 'quiz-v2__option quiz-v2__option--multi';
      if (selected.indexOf(option.short) !== -1) btn.classList.add('quiz-v2__option--selected');
      btn.dataset.value = option.short;
      btn.textContent = option.text;

      btn.addEventListener('click', () => {
        if (option.exclusive) {
          // Exclusive: clear all others, select only this one.
          selected.length = 0;
          selected.push(option.short);
          optionsEl.querySelectorAll('.quiz-v2__option').forEach(b => {
            b.classList.toggle('quiz-v2__option--selected', b.dataset.value === option.short);
          });
        } else {
          // Non-exclusive: deselect any active exclusive option first.
          q.options.forEach(o => {
            if (o.exclusive) {
              const idx = selected.indexOf(o.short);
              if (idx !== -1) {
                selected.splice(idx, 1);
                const exBtn = optionsEl.querySelector(`[data-value="${o.short}"]`);
                if (exBtn) exBtn.classList.remove('quiz-v2__option--selected');
              }
            }
          });
          const idx = selected.indexOf(option.short);
          if (idx !== -1) {
            selected.splice(idx, 1);
            btn.classList.remove('quiz-v2__option--selected');
          } else {
            selected.push(option.short);
            btn.classList.add('quiz-v2__option--selected');
          }
        }
        nextBtn.disabled = selected.length === 0;
      });

      optionsEl.appendChild(btn);
    });

    root.appendChild(optionsEl);
    root.appendChild(nextBtn);
  }

  // ── Ranked multi-select ────────────────────────────────────────────────────

  function renderRankedMultiSelect(q, root, savedAnswer) {
    const ranked = Array.isArray(savedAnswer) ? savedAnswer.slice() : [];
    const maxRank = q.maxRank || 6;

    const optionsEl = document.createElement('div');
    optionsEl.className = 'quiz-v2__options quiz-v2__options--multi quiz-v2__options--ranked';
    if (q.twoColumn) optionsEl.classList.add('quiz-v2__options--two-col');

    const nextBtn = document.createElement('button');
    nextBtn.type = 'button';
    nextBtn.className = 'quiz-v2__next-btn';
    nextBtn.textContent = 'Continue';
    nextBtn.disabled = ranked.length === 0;
    nextBtn.addEventListener('click', function () {
      if (ranked.length > 0) QuizNavigation.next(ranked.slice());
    });

    function updateOptionStates() {
      const atCap = ranked.length >= maxRank;
      optionsEl.querySelectorAll('.quiz-v2__option--ranked').forEach(function (btn) {
        const val = btn.dataset.value;
        const rankPos = ranked.indexOf(val);
        const isSelected = rankPos !== -1;
        btn.classList.toggle('quiz-v2__option--selected', isSelected);
        btn.classList.toggle('quiz-v2__option--muted', !isSelected && atCap);
        const indicator = btn.querySelector('.quiz-v2__rank-indicator');
        if (indicator) {
          indicator.textContent = isSelected ? String(rankPos + 1) : '';
          indicator.style.visibility = isSelected ? 'visible' : 'hidden';
        }
      });
      nextBtn.disabled = ranked.length === 0;
    }

    // Shuffle display order once per quiz session (stable across back-navigation)
    if (!q._shuffledOptions) {
      q._shuffledOptions = q.options.slice();
      for (var i = q._shuffledOptions.length - 1; i > 0; i--) {
        var j = Math.floor(Math.random() * (i + 1));
        var tmp = q._shuffledOptions[i]; q._shuffledOptions[i] = q._shuffledOptions[j]; q._shuffledOptions[j] = tmp;
      }
    }

    q._shuffledOptions.forEach(function (option) {
      var btn = document.createElement('button');
      btn.type = 'button';
      btn.className = 'quiz-v2__option quiz-v2__option--ranked';
      btn.dataset.value = option.short;

      var rankPos = ranked.indexOf(option.short);
      if (rankPos !== -1) btn.classList.add('quiz-v2__option--selected');
      if (ranked.length >= maxRank && rankPos === -1) btn.classList.add('quiz-v2__option--muted');

      // Icon (if provided) — sits on the left
      if (option.icon) {
        var iconEl = document.createElement('img');
        iconEl.className = 'quiz-v2__option-icon';
        iconEl.src = option.icon;
        iconEl.alt = '';
        btn.appendChild(iconEl);
      }

      var label = document.createElement('span');
      label.className = 'quiz-v2__option-label';
      label.textContent = option.text;
      btn.appendChild(label);

      // Rank indicator — sits on the right
      var indicator = document.createElement('span');
      indicator.className = 'quiz-v2__rank-indicator';
      indicator.textContent = rankPos !== -1 ? String(rankPos + 1) : '';
      indicator.style.visibility = rankPos !== -1 ? 'visible' : 'hidden';
      btn.appendChild(indicator);

      btn.addEventListener('click', function () {
        const idx = ranked.indexOf(option.short);
        if (idx !== -1) {
          ranked.splice(idx, 1);
        } else if (ranked.length < maxRank) {
          ranked.push(option.short);
        }
        // At cap and not selected — ignore click.
        updateOptionStates();
      });

      optionsEl.appendChild(btn);
    });

    root.appendChild(optionsEl);
    root.appendChild(nextBtn);
  }

  // ── Text input ─────────────────────────────────────────────────────────────

  function renderTextInput(q, root, savedAnswer) {
    const isEmail = q.validation && q.validation.email;

    const input = document.createElement('input');
    input.type = isEmail ? 'email' : 'text';
    input.className = 'quiz-v2__text-input';
    input.value = savedAnswer || '';
    if (isEmail) {
      input.placeholder = 'Your email address';
    }
    if (q.validation) {
      if (q.validation.minLength) input.minLength = q.validation.minLength;
      if (q.validation.maxLength) input.maxLength = q.validation.maxLength;
    }

    const errorEl = document.createElement('p');
    errorEl.className = 'quiz-v2__input-error';
    errorEl.setAttribute('aria-live', 'polite');
    errorEl.style.display = 'none';

    // Final email step: consent checkbox + coral submit button
    var isEmailFinal = q.short === 'email';
    var consentCheckbox = null;
    var consentError = null;
    var consentWrap = null;

    if (isEmailFinal) {
      consentWrap = document.createElement('div');
      consentWrap.className = 'quiz-v2__consent';

      consentCheckbox = document.createElement('input');
      consentCheckbox.type = 'checkbox';
      consentCheckbox.id = 'quiz-v2-consent';
      consentCheckbox.className = 'quiz-v2__consent-checkbox';

      var consentLabel = document.createElement('label');
      consentLabel.htmlFor = 'quiz-v2-consent';
      consentLabel.className = 'quiz-v2__consent-label';
      var privacyUrl = q.privacyPolicyUrl || 'https://vitable.com.au/policies/privacy-policy';
      consentLabel.innerHTML =
        'By checking this box, you confirm that you have read and agreed to our ' +
        '<a href="' + privacyUrl + '" target="_blank" rel="noopener noreferrer">Privacy Policy</a>.' +
        '<br>By choosing \u2018See my results\u2019, you also consent to receiving our communications and exclusive offers and can unsubscribe at any time.';

      consentWrap.appendChild(consentCheckbox);
      consentWrap.appendChild(consentLabel);

      consentError = document.createElement('p');
      consentError.className = 'quiz-v2__input-error';
      consentError.setAttribute('aria-live', 'polite');
      consentError.style.display = 'none';

      consentCheckbox.addEventListener('change', function () {
        if (consentError) consentError.style.display = 'none';
      });
    }

    const nextBtn = document.createElement('button');
    nextBtn.type = 'button';
    nextBtn.className = isEmailFinal ? 'quiz-v2__next-btn quiz-v2__submit-btn' : 'quiz-v2__next-btn';
    nextBtn.textContent = isEmailFinal ? 'See my results' : 'Next';

    nextBtn.addEventListener('click', () => {
      const val = input.value.trim();
      if (isEmail) {
        if (!val.includes('@') || !val.includes('.')) {
          errorEl.textContent = 'Please enter a valid email address';
          errorEl.style.display = '';
          input.focus();
          return;
        }
      } else if (q.validation && q.validation.minLength && val.length < q.validation.minLength) {
        errorEl.textContent = 'Please enter at least ' + q.validation.minLength + ' character' + (q.validation.minLength > 1 ? 's' : '') + '.';
        errorEl.style.display = '';
        input.focus();
        return;
      } else if (q.validation && q.validation.pattern && !q.validation.pattern.test(val)) {
        errorEl.textContent = 'Please use only letters, numbers, spaces, hyphens or apostrophes.';
        errorEl.style.display = '';
        input.focus();
        return;
      }
      errorEl.style.display = 'none';
      // Consent validation (final email step only)
      if (isEmailFinal && consentCheckbox && !consentCheckbox.checked) {
        consentError.textContent = 'Please agree to the Privacy Policy to continue.';
        consentError.style.display = '';
        return;
      }
      if (consentError) consentError.style.display = 'none';
      QuizNavigation.next(val);
    });

    input.addEventListener('input', () => { errorEl.style.display = 'none'; });
    input.addEventListener('keydown', e => { if (e.key === 'Enter') nextBtn.click(); });

    root.appendChild(input);
    root.appendChild(errorEl);
    if (consentWrap) root.appendChild(consentWrap);
    if (consentError) root.appendChild(consentError);
    root.appendChild(nextBtn);
    input.focus();
  }

  // ── Number input ───────────────────────────────────────────────────────────

  function renderNumberInput(q, root, savedAnswer) {
    const input = document.createElement('input');
    input.type = 'text';
    input.inputMode = 'numeric';
    input.pattern = '[0-9]*';
    input.className = 'quiz-v2__number-input';
    input.value = savedAnswer || '';

    const errorEl = document.createElement('p');
    errorEl.className = 'quiz-v2__input-error';
    errorEl.setAttribute('aria-live', 'polite');
    errorEl.style.display = 'none';

    function showError(msg) {
      errorEl.textContent = msg;
      errorEl.style.display = '';
    }

    function clearError() {
      errorEl.textContent = '';
      errorEl.style.display = 'none';
    }

    const nextBtn = document.createElement('button');
    nextBtn.type = 'button';
    nextBtn.className = 'quiz-v2__next-btn';
    nextBtn.textContent = 'Continue';

    nextBtn.addEventListener('click', () => {
      const val = input.value.trim();
      const num = parseInt(val, 10);
      if (isNaN(num) || String(num) !== val) {
        showError('Please enter a whole number.');
        return;
      }
      if (q.validation) {
        if (q.validation.min !== undefined && num < q.validation.min) {
          if (q.short === 'age' && num < 18) {
            showError('Sorry, the quiz is only suitable for people 18 years and over');
          } else {
            showError('Please enter a value of ' + q.validation.min + ' or more.');
          }
          return;
        }
        if (q.validation.max !== undefined && num > q.validation.max) {
          if (q.short === 'age') {
            showError('Please enter an age between 18 and 100');
          } else {
            showError('Please enter a value of ' + q.validation.max + ' or less.');
          }
          return;
        }
      }
      clearError();
      // Submitted as string to match backend payload format.
      QuizNavigation.next(String(num));
    });

    input.addEventListener('input', clearError);
    input.addEventListener('keydown', e => { if (e.key === 'Enter') nextBtn.click(); });

    root.appendChild(input);
    root.appendChild(errorEl);
    root.appendChild(nextBtn);
    input.focus();
  }

  // ── Section screen ─────────────────────────────────────────────────────────

  // The four transition step labels. If q.short matches one, render a step
  // indicator instead of a plain heading.
  var TRANSITION_STEPS = ['Basics', 'Goals', 'Diet', 'Lifestyle'];
  var TRANSITION_SHORTS = ['basics', 'goals', 'diet', 'lifestyle'];
  var STEP_CIRCLE_SVG = 'https://cdn.shopify.com/s/files/1/0914/5070/4171/files/selected-quiz-step.svg?v=1755740055';

  function renderTransitionSteps(activeLabel, root) {
    var container = document.createElement('div');
    container.className = 'quiz-v2__steps';

    TRANSITION_STEPS.forEach(function (label) {
      var step = document.createElement('div');
      step.className = 'quiz-v2__step';
      if (label === activeLabel) {
        step.classList.add('quiz-v2__step--active');
        var circle = document.createElement('img');
        circle.className = 'quiz-v2__step-circle';
        circle.src = STEP_CIRCLE_SVG;
        circle.alt = '';
        step.appendChild(circle);
      }
      var span = document.createElement('span');
      span.textContent = label;
      step.appendChild(span);
      container.appendChild(step);
    });

    root.appendChild(container);
  }

  function renderSection(q, root) {
    if (sectionAdvanceTimer !== null) {
      clearTimeout(sectionAdvanceTimer);
      sectionAdvanceTimer = null;
    }

    // Check if this is a transition screen (basics/goals/diet/lifestyle)
    var transIdx = TRANSITION_SHORTS.indexOf(q.short);
    if (transIdx !== -1) {
      renderTransitionSteps(TRANSITION_STEPS[transIdx], root);
      sectionAdvanceTimer = setTimeout(function () {
        sectionAdvanceTimer = null;
        QuizNavigation.next(null);
      }, 1500);
      return;
    }

    // get_started: fire start_quiz analytics immediately, then auto-advance
    if (q.short === 'get_started') {
      var heading = document.createElement('h2');
      heading.className = 'quiz-v2__section-heading';
      var greetingText = (window.QUIZ_V2_RETAKE === true) ? 'Nice to see you again, {first_name}!' : q.text;
      heading.textContent = resolveText(greetingText);
      root.appendChild(heading);
      QuizAnalytics.onQuizStart();
      sectionAdvanceTimer = setTimeout(function () {
        sectionAdvanceTimer = null;
        QuizNavigation.next(null);
      }, 1500);
      return;
    }

    // Default section rendering (welcome, disclaimer, etc.)
    var heading = document.createElement('h2');
    heading.className = 'quiz-v2__section-heading';
    heading.innerHTML = resolveText(q.text);
    root.appendChild(heading);

    if (q.subtext) {
      var subtextEl = document.createElement('div');
      subtextEl.className = q.short === 'disclaimer'
        ? 'quiz-v2__subtext quiz-v2__subtext--disclaimer'
        : 'quiz-v2__subtext';
      subtextEl.innerHTML = linkifySubtext(q.subtext, q.privacyPolicyUrl);
      root.appendChild(subtextEl);
    }

    if (q.image) {
      var img = document.createElement('img');
      img.className = 'quiz-v2__section-image';
      img.src = q.image;
      img.alt = '';
      root.appendChild(img);
    }

    if (q.badge) {
      var badge = document.createElement('div');
      badge.className = 'quiz-v2__badge';
      var badgeIcon = document.createElement('img');
      badgeIcon.src = q.badge.icon;
      badgeIcon.alt = '';
      badgeIcon.className = 'quiz-v2__badge-icon';
      var badgeText = document.createElement('span');
      badgeText.className = 'quiz-v2__badge-text';
      badgeText.textContent = q.badge.text;
      badge.appendChild(badgeIcon);
      badge.appendChild(badgeText);
      root.appendChild(badge);
    }

    if (q.cta) {
      var ctaBtn = document.createElement('button');
      ctaBtn.type = 'button';
      ctaBtn.className = q.short === 'welcome' ? 'quiz-v2__get-started-btn' : 'quiz-v2__cta-btn';
      ctaBtn.textContent = q.cta;
      ctaBtn.addEventListener('click', function () {
        if (sectionAdvanceTimer !== null) {
          clearTimeout(sectionAdvanceTimer);
          sectionAdvanceTimer = null;
        }
        QuizNavigation.next(null);
      });
      root.appendChild(ctaBtn);
    } else {
      // Auto-advance after 1500ms for transition screens.
      sectionAdvanceTimer = setTimeout(function () {
        sectionAdvanceTimer = null;
        QuizNavigation.next(null);
      }, 1500);
    }
  }

  // ── Main render dispatcher ─────────────────────────────────────────────────

  function renderQuestion(question, root) {
    if (sectionAdvanceTimer !== null) {
      clearTimeout(sectionAdvanceTimer);
      sectionAdvanceTimer = null;
    }

    root.innerHTML = '';

    // Heading for non-section types (section renders its own heading).
    if (question.type !== 'section') {
      const heading = document.createElement('h2');
      heading.className = 'quiz-v2__question-text';
      heading.textContent = resolveText(question.text);
      root.appendChild(heading);

      // Optional description text below heading (config-driven)
      if (question.description) {
        const desc = document.createElement('p');
        desc.className = 'quiz-v2__description';
        desc.textContent = question.description;
        root.appendChild(desc);
      }
    }

    const savedAnswer = QuizState.answers[question.short];

    switch (question.type) {
      case 'single-choice':
        renderSingleChoice(question, root, savedAnswer);
        break;
      case 'multi-select':
        renderMultiSelect(question, root, savedAnswer);
        break;
      case 'ranked-multi-select':
        renderRankedMultiSelect(question, root, savedAnswer);
        break;
      case 'text-input':
        renderTextInput(question, root, savedAnswer);
        break;
      case 'number-input':
        renderNumberInput(question, root, savedAnswer);
        break;
      case 'section':
        renderSection(question, root);
        break;
      default: {
        const fallback = document.createElement('p');
        fallback.textContent = `Unknown question type: ${question.type}`;
        root.appendChild(fallback);
      }
    }

    // Back button — not shown on first question or on section screens.
    if (QuizState.currentIndex > 0 && question.type !== 'section') {
      const backBtn = document.createElement('button');
      backBtn.type = 'button';
      backBtn.className = 'quiz-v2__back-btn';
      backBtn.textContent = 'Back';
      backBtn.addEventListener('click', () => QuizNavigation.back());
      root.appendChild(backBtn);
    }

    // Update progress tracker (visual wired in PR-T4).
    const total = QuizState.activeQuestions.filter(q => q.type !== 'section').length;
    const answered = QuizState.activeQuestions
      .slice(0, QuizState.currentIndex)
      .filter(q => q.type !== 'section').length;
    renderProgress(answered, total, question.type === 'section');
  }

  return { renderQuestion, renderProgress };
})();

// ----------------------------------------------------------------------------
// QuizNavigation — forward/back navigation with downstream answer invalidation
// ----------------------------------------------------------------------------

const QuizNavigation = (function () {
  function next(answer) {
    const currentQuestion = QuizState.activeQuestions[QuizState.currentIndex];

    // Record the answer — sections don't record anything.
    if (currentQuestion && currentQuestion.type !== 'section' && answer !== null) {
      QuizState.answers[currentQuestion.short] = answer;
    }

    // Snapshot the current active shorts before rebuilding.
    const prevShorts = QuizState.activeQuestions.map(q => q.short);

    // Rebuild the active list with the updated answers.
    const isRetake = window.QUIZ_V2_RETAKE === true;
    QuizState.activeQuestions = QuizCriteria.buildActiveList(
      QUIZ_CONFIG.questions,
      QuizState.answers,
      isRetake
    );

    // Remove answers for any questions no longer in the active list.
    var newShorts = QuizState.activeQuestions.map(q => q.short);
    invalidateDownstream(prevShorts, newShorts);

    // Second pass — needed for multi-level conditional chains (depth ≥ 2).
    // The first pass may leave deeper dependents in the active list because
    // their intermediate parent answer was still present during rebuild.
    // After the first invalidateDownstream removes that parent answer, this
    // second rebuild correctly excludes the orphaned deeper questions too.
    var passOneShorts = newShorts;
    QuizState.activeQuestions = QuizCriteria.buildActiveList(
      QUIZ_CONFIG.questions,
      QuizState.answers,
      isRetake
    );
    newShorts = QuizState.activeQuestions.map(q => q.short);
    invalidateDownstream(passOneShorts, newShorts);

    // Find where the just-answered question sits in the new active list and advance.
    const posInNew = QuizState.activeQuestions.findIndex(q => q === currentQuestion);
    const baseIndex = posInNew !== -1 ? posInNew : QuizState.currentIndex;
    QuizState.currentIndex = baseIndex + 1;

    QuizSession.save(QuizState.answers, QuizState.currentIndex);

    const root = document.getElementById('quiz-v2-root');

    if (QuizState.currentIndex >= QuizState.activeQuestions.length) {
      // End of quiz — trigger submission.
      QuizSubmit.submit();
      return;
    }

    if (root) QuizRenderer.renderQuestion(QuizState.activeQuestions[QuizState.currentIndex], root);
  }

  function back() {
    if (QuizState.currentIndex <= 0) return;

    // Step backward, skipping any section/transition screens.
    // Forward flow is unaffected — sections still auto-advance normally.
    let newIndex = QuizState.currentIndex - 1;
    while (newIndex > 0 && QuizState.activeQuestions[newIndex].type === 'section') {
      newIndex -= 1;
    }

    // If we land on a section at index 0 (the welcome screen), don't go back.
    if (QuizState.activeQuestions[newIndex].type === 'section') {
      return;
    }

    QuizState.currentIndex = newIndex;
    QuizSession.save(QuizState.answers, QuizState.currentIndex);
    const root = document.getElementById('quiz-v2-root');
    if (root) QuizRenderer.renderQuestion(QuizState.activeQuestions[QuizState.currentIndex], root);
  }

  // Remove answers for questions that were active before but are no longer active.
  function invalidateDownstream(prevShorts, newActiveShorts) {
    prevShorts.forEach(short => {
      if (newActiveShorts.indexOf(short) === -1) {
        delete QuizState.answers[short];
      }
    });
  }

  return { next, back, invalidateDownstream };
})();

// ----------------------------------------------------------------------------
// QuizEngine — initialisation, resume prompt, error boundary
// ----------------------------------------------------------------------------

const QuizEngine = (function () {
  function showResumePrompt(root, savedState) {
    root.innerHTML = '';

    const wrapper = document.createElement('div');
    wrapper.className = 'quiz-v2__resume-prompt';

    const msg = document.createElement('p');
    msg.className = 'quiz-v2__resume-message';
    msg.textContent = 'Continue where you left off?';
    wrapper.appendChild(msg);

    const continueBtn = document.createElement('button');
    continueBtn.type = 'button';
    continueBtn.className = 'quiz-v2__resume-btn quiz-v2__resume-btn--continue';
    continueBtn.textContent = 'Yes, continue';
    continueBtn.addEventListener('click', () => {
      QuizState.answers = savedState.answers;
      // Always re-apply logged-in first_name so it stays current even if
      // the resumed session pre-dates PR-T8.
      if (window.QUIZ_V2_RETAKE && window.__vitableCustomer) {
        QuizState.answers.first_name = window.__vitableCustomer.firstName || '';
      }
      QuizState.currentIndex = typeof savedState.currentIndex === 'number' ? savedState.currentIndex : 0;
      const isRetake = window.QUIZ_V2_RETAKE === true;
      QuizState.activeQuestions = QuizCriteria.buildActiveList(
        QUIZ_CONFIG.questions,
        QuizState.answers,
        isRetake
      );
      // Clamp to valid range in case config changed since session was saved.
      if (QuizState.currentIndex >= QuizState.activeQuestions.length) {
        QuizState.currentIndex = Math.max(0, QuizState.activeQuestions.length - 1);
      }
      QuizRenderer.renderQuestion(QuizState.activeQuestions[QuizState.currentIndex], root);
    });
    wrapper.appendChild(continueBtn);

    const freshBtn = document.createElement('button');
    freshBtn.type = 'button';
    freshBtn.className = 'quiz-v2__resume-btn quiz-v2__resume-btn--fresh';
    freshBtn.textContent = 'Start over';
    freshBtn.addEventListener('click', () => {
      QuizSession.clear();
      QuizState.answers = {};
      QuizState.currentIndex = 0;
      const isRetake = window.QUIZ_V2_RETAKE === true;
      // Pre-populate first_name for retake — same as fresh-start in init().
      if (isRetake && window.__vitableCustomer) {
        QuizState.answers.first_name = window.__vitableCustomer.firstName || '';
      }
      QuizState.activeQuestions = QuizCriteria.buildActiveList(
        QUIZ_CONFIG.questions,
        QuizState.answers,
        isRetake
      );
      QuizRenderer.renderQuestion(QuizState.activeQuestions[0], root);
    });
    wrapper.appendChild(freshBtn);

    root.appendChild(wrapper);
  }

  function init() {
    const root = document.getElementById('quiz-v2-root');
    const loadingEl = document.getElementById('quiz-v2-loading');

    try {
      if (typeof QUIZ_CONFIG === 'undefined' || !Array.isArray(QUIZ_CONFIG.questions)) {
        throw new Error('QUIZ_CONFIG is not defined — ensure quiz-v2-config.js loads before quiz-v2.js.');
      }

      const isRetake = window.QUIZ_V2_RETAKE === true;
      const savedState = QuizSession.restore();

      if (savedState && savedState.answers && Object.keys(savedState.answers).length > 0) {
        if (root) showResumePrompt(root, savedState);
      } else {
        QuizState.answers = {};
        // Pre-populate first_name from Shopify customer data on retake.
        // The question is skipForLoggedIn so it won't be shown, but the answer
        // must be in state for {first_name} tokens and the submission payload.
        if (isRetake && window.__vitableCustomer) {
          QuizState.answers.first_name = window.__vitableCustomer.firstName || '';
        }
        QuizState.currentIndex = 0;
        QuizState.activeQuestions = QuizCriteria.buildActiveList(
          QUIZ_CONFIG.questions,
          QuizState.answers,
          isRetake
        );
        if (root) QuizRenderer.renderQuestion(QuizState.activeQuestions[0], root);
      }

      if (loadingEl) loadingEl.style.display = 'none';

    } catch (err) {
      console.error('[QuizV2] Engine initialisation failed:', err);
      if (loadingEl) loadingEl.style.display = 'none';
      if (root) {
        root.innerHTML =
          '<div class="quiz-v2__error">' +
          '<p>Sorry, something went wrong loading the quiz. Please refresh the page to try again.</p>' +
          '</div>';
      }
    }
  }

  return { init };
})();

// ----------------------------------------------------------------------------
// QuizAnalytics — GA4, Meta Pixel, TikTok, Insider event firing
// ----------------------------------------------------------------------------

const QuizAnalytics = (function () {
  // ── Private helpers ────────────────────────────────────────────────────────

  // Injects the Insider ins.js script — not loaded globally, must be added by
  // the quiz itself. Account ID 10007631. Copied from quiz.js createInsiderTag.
  // [DISABLED] Insider SDK injection — no Insider events should fire
  function createInsiderTag() {
    return; // Insider disabled
    var script = document.createElement('script');
    script.src = '//vitable.api.useinsider.com/ins.js?id=10007631';
    script.async = true;
    script.type = 'text/javascript';
    document.head.appendChild(script);
  }

  // Unique ID for Meta Pixel event deduplication. Copied from quiz.js uid().
  function uid() {
    try { return crypto.randomUUID(); }
    catch (e) { return 'quiz-' + Date.now() + '-' + Math.random().toString(36).slice(2); }
  }

  // UTC ISO-8601 formatter used by Insider. Copied from quiz.js isoDateString().
  function pad(n) {
    return n < 10 ? '0' + n : n;
  }

  function isoDateString(d) {
    return d.getUTCFullYear() + '-' +
      pad(d.getUTCMonth() + 1) + '-' +
      pad(d.getUTCDate()) + 'T' +
      pad(d.getUTCHours()) + ':' +
      pad(d.getUTCMinutes()) + ':' +
      pad(d.getUTCSeconds()) + 'Z';
  }

  // Sets insider_object.user. Copied from quiz.js loadInsiderUser().
  // ins.js is pre-loaded in init() — no script injection here.
  function loadInsiderUser(email, firstName) {
    window.insider_object.user = {
      uuid: '',
      email,
      name: firstName,
      surname: '',
      custom: { user_id: '', test_multi_1: { recommended: true, current_order: false } },
      username: email,
      gdpr_optin: true,
      email_optin: true
    };
  }

  // ── Public methods ─────────────────────────────────────────────────────────

  // TikTok pixel bootstrap + Insider script injection — call at script load
  // time before DOM ready so both SDKs have the full quiz duration to load.
  function init() {
    // TikTok — copied from quiz.js initTikTokEarly IIFE (pixel ID C2LDHICST061L8G6PMJ0).
    if (typeof window !== 'undefined' && !window.ttq) {
      !function (w, d, t) {
        w.TiktokAnalyticsObject=t;var ttq=w[t]=w[t]||[];ttq.methods=["page","track","identify","instances","debug","on","off","once","ready","alias","group","enableCookie","disableCookie"],ttq.setAndDefer=function(t,e){t[e]=function(){t.push([e].concat(Array.prototype.slice.call(arguments,0)))}};for(var i=0;i<ttq.methods.length;i++)ttq.setAndDefer(ttq,ttq.methods[i]);ttq.instance=function(t){for(var e=ttq._i[t]||[],n=0;n<ttq.methods.length;n++)ttq.setAndDefer(e,ttq.methods[n]);return e},ttq.load=function(e,n){var r="https://analytics.tiktok.com/i18n/pixel/events.js";ttq._i=ttq._i||{},ttq._i[e]=[],ttq._i[e]._u=r,ttq._t=ttq._t||{},ttq._t[e]=+new Date,ttq._o=ttq._o||{},ttq._o[e]=n||{}};
        ttq.load('C2LDHICST061L8G6PMJ0');
        ttq.page();
      }(window, document, 'ttq');
      const script = document.createElement('script');
      script.src = 'https://analytics.tiktok.com/i18n/pixel/events.js?sdkid=C2LDHICST061L8G6PMJ0&lib=ttq';
      script.async = true;
      document.head.appendChild(script);
    }
    // [DISABLED] Insider — SDK no longer pre-loaded on quiz page
    // createInsiderTag();
  }

  // GA4 start_quiz — fired when user clicks the Get Started button.
  function onQuizStart() {
    try {
      if (typeof gtag === 'function') gtag('event', 'start_quiz', {});
    } catch (e) {
      console.warn('[QuizV2] GA4 start_quiz failed:', e);
    }
  }

  // All four platform events — fired on successful backend submission.
  // Each platform is wrapped independently so one failure cannot block others.
  function onSubmitSuccess(email, firstName) {
    try {
      if (typeof gtag === 'function') gtag('event', 'submit_quiz', {});
    } catch (e) {
      console.warn('[QuizV2] GA4 submit_quiz failed:', e);
    }

    try {
      if (typeof fbq === 'function') fbq('track', 'Lead', {}, { eventID: uid() });
    } catch (e) {
      console.warn('[QuizV2] Meta Lead failed:', e);
    }

    try {
      if (window.ttq && typeof window.ttq.track === 'function') {
        window.ttq.track('SubmitForm', {
          content_type: 'quiz_submission',
          content_id: 'vitable_quiz',
          description: 'Vitable Quiz Submission'
        });
      }
    } catch (e) {
      console.warn('[QuizV2] TikTok SubmitForm failed:', e);
    }

    /* [DISABLED] Insider quiz_submitted — no longer fires */
    if (false) {
    try {
      loadInsiderUser(email, firstName);
      if (window.Insider && typeof window.Insider.track === 'function') {
        window.Insider.eventManager.dispatch('init-manager:re-initialize');
        window.Insider.track('events', [{
          event_name: 'quiz_submitted',
          timestamp: isoDateString(new Date()),
          event_params: {
            custom: {
              event_source: 'quiz_page'
            }
          }
        }]);
      }
    } catch (e) {
      console.warn('[QuizV2] Insider quiz_submitted failed:', e);
    }
    } // end [DISABLED]
  }

  return { init, onQuizStart, onSubmitSuccess };
})();

// ----------------------------------------------------------------------------
// QuizSubmit — payload assembly, backend POST, localStorage writes
// ----------------------------------------------------------------------------

const QuizSubmit = (function () {
  const ENDPOINT = '/apps/api/prod/recommendation/quiz/submission';
  const TIMEOUT_MS = 15000;

  let __vitableQuizSubmitted = false;

  // Converts raw quiz state answers to the backend payload shape.
  // - String values: passed through as-is
  // - Array values: joined with ', ' (comma-space) per backend split regex /,\s*/
  //   EXCEPTION: improve_area joined with ',' (no space) to preserve rank order
  // - Null/undefined/empty: omitted
  function buildPayload() {
    const rawAnswers = QuizState.answers;
    const assembled = {};

    Object.keys(rawAnswers).forEach(function (short) {
      const val = rawAnswers[short];
      if (Array.isArray(val)) {
        assembled[short] = short === 'improve_area'
          ? val.join(',')
          : val.join(', ');
      } else if (val !== null && val !== undefined && val !== '') {
        assembled[short] = val;
      }
    });

    const isLoggedIn = window.QUIZ_V2_RETAKE === true;
    const email = isLoggedIn
      ? (window.__vitableCustomer && window.__vitableCustomer.email) || ''
      : assembled.email || '';
    // Remove email from answers — it goes to the top-level email field, not
    // inside the answers object (backend contract).
    delete assembled.email;
    const payload = {
      email,
      isLoggedIn,
      answers: assembled,
    };
    if (isLoggedIn) {
      payload.customerEmail = email;
    }
    return payload;
  }

  // Writes the four required localStorage keys.
  // Receives the raw QuizState.answers (pre-assembly).
  // Arrays are joined with ',' (no space) for localStorage consumers.
  function writeLocalStorage(rawAnswers) {
    function toLocalStorageString(val) {
      if (Array.isArray(val)) return val.join(',');
      return (val !== null && val !== undefined) ? String(val) : '';
    }

    var pregnancy = rawAnswers.pregnancy;
    if (pregnancy && pregnancy !== '') {
      localStorage.setItem('vitable_pregnancy', toLocalStorageString(pregnancy));
    } else {
      localStorage.removeItem('vitable_pregnancy');
    }

    var allergy = rawAnswers.allergy;
    if (allergy && (!Array.isArray(allergy) ? allergy !== '' : allergy.length > 0)) {
      localStorage.setItem('vitable_allergy', toLocalStorageString(allergy));
    } else {
      localStorage.removeItem('vitable_allergy');
    }

    var firstName = rawAnswers.first_name;
    if (firstName && firstName !== '') {
      localStorage.setItem('vitable_name_on_pack', toLocalStorageString(firstName));
    } else {
      localStorage.removeItem('vitable_name_on_pack');
    }

    // Always written — epoch milliseconds as string (NOT ISO date string).
    localStorage.setItem('vitable_latest_quiz_date', Date.now().toString());
  }

  function showOverlay() {
    var overlay = document.getElementById('quiz-v2-submit-overlay');
    if (!overlay) return;
    if (!overlay.querySelector('.quiz-v2__spinner')) {
      var spinner = document.createElement('div');
      spinner.className = 'quiz-v2__spinner';
      overlay.appendChild(spinner);
    }
    overlay.style.display = 'flex';
  }

  function hideOverlay() {
    var overlay = document.getElementById('quiz-v2-submit-overlay');
    if (overlay) overlay.style.display = 'none';
  }

  function showError(root, retryFn) {
    root.innerHTML = '';

    var wrapper = document.createElement('div');
    wrapper.className = 'quiz-v2__error';

    var msg = document.createElement('p');
    msg.textContent = 'Something went wrong. Please try again.';
    wrapper.appendChild(msg);

    var retryBtn = document.createElement('button');
    retryBtn.type = 'button';
    retryBtn.className = 'quiz-v2__next-btn';
    retryBtn.textContent = 'Try again';
    retryBtn.addEventListener('click', retryFn);
    wrapper.appendChild(retryBtn);

    root.appendChild(wrapper);
  }

  function submit() {
    if (__vitableQuizSubmitted) return;
    __vitableQuizSubmitted = true;

    var root = document.getElementById('quiz-v2-root');
    showOverlay();

    var payload = buildPayload();
    var controller = new AbortController();
    var timeoutId = setTimeout(function () { controller.abort(); }, TIMEOUT_MS);

    fetch(ENDPOINT, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      credentials: 'same-origin',
      body: JSON.stringify(payload),
      signal: controller.signal,
    })
      .then(function (response) {
        clearTimeout(timeoutId);
        if (!response.ok) throw new Error('Server returned ' + response.status);
        return response.json();
      })
      .then(function (data) {
        var token = data.token;
        if (!token) throw new Error('Missing recommendation token in response');

        var email = payload.email;
        var firstName = QuizState.answers.first_name || '';

        // Analytics — fire before localStorage writes and redirect.
        QuizAnalytics.onSubmitSuccess(email, firstName);

        // Write localStorage keys synchronously before redirect.
        writeLocalStorage(QuizState.answers);

        // Clear quiz session — submission succeeded.
        QuizSession.clear();

        window.location.href = '/pages/recommendation?r=' + encodeURIComponent(token);
      })
      .catch(function (err) {
        clearTimeout(timeoutId);
        console.error('[QuizV2] Submission failed:', err);
        hideOverlay();
        // Reset guard so user can retry.
        __vitableQuizSubmitted = false;
        if (root) showError(root, submit);
      });
  }

  return { submit };
})();

QuizAnalytics.init();
document.addEventListener('DOMContentLoaded', QuizEngine.init);
